import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Link2, ExternalLink } from 'lucide-react';

const NONE = '__none__';

/** Append a tracking ref to a funnel's public URL so submitted leads are
 *  attributable to the post/campaign that drove them. */
export const buildTrackedUrl = (funnel, ref) => {
  if (!funnel?.public_url) return '';
  const sep = funnel.public_url.includes('?') ? '&' : '?';
  return ref ? `${funnel.public_url}${sep}ref=${encodeURIComponent(ref)}` : funnel.public_url;
};

/**
 * Dropdown of the user's PUBLISHED funnel pages. Calls onChange(funnel|null).
 * Renders nothing useful (a hint) when the user has no published pages.
 */
export const FunnelCtaPicker = ({ value, onChange, label = 'Lead-capture CTA (optional)', refSource }) => {
  const { api } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/funnels')
      .then((r) => { if (mounted) setFunnels((r.data?.funnels || []).filter((f) => f.status === 'published')); })
      .catch(() => { if (mounted) setFunnels([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []); // eslint-disable-line

  const selected = funnels.find((f) => f.id === value) || null;

  return (
    <div data-testid="funnel-cta-picker">
      <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-pink-600" /> {label}</Label>
      <Select
        value={value || NONE}
        onValueChange={(v) => onChange(v === NONE ? null : (funnels.find((f) => f.id === v) || null))}
      >
        <SelectTrigger data-testid="funnel-cta-select" className="mt-1">
          <SelectValue placeholder={loading ? 'Loading pages…' : (funnels.length ? 'No CTA' : 'No published pages')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>No CTA link</SelectItem>
          {funnels.map((f) => (
            <SelectItem key={f.id} value={f.id}>{f.title} — /p/{f.slug}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected ? (
        <a href={buildTrackedUrl(selected, refSource)} target="_blank" rel="noreferrer"
           className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1 break-all">
          {buildTrackedUrl(selected, refSource)} <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        !loading && funnels.length === 0 && (
          <p className="text-xs text-zinc-400 mt-1">Create a page in Funnel Builder to drive leads from this post.</p>
        )
      )}
    </div>
  );
};

export default FunnelCtaPicker;
